/**
 * Drop-in <button> wrapper that emits an analytics envelope on click.
 * Visual output is identical to a native <button>; consumers keep their
 * own className/style.
 */
import { forwardRef, type ComponentProps, type MouseEvent } from 'react'
import type { ComponentType } from './types'
import { useTrack } from './useTrack'

export interface TrackedButtonProps extends ComponentProps<'button'> {
  trackId: string
  trackAction?: string
  trackType?: ComponentType
  trackMeta?: Record<string, string | number | boolean>
}

export const TrackedButton = forwardRef<HTMLButtonElement, TrackedButtonProps>(
  function TrackedButton(
    { trackId, trackAction = 'tap', trackType = 'button', trackMeta, onClick, ...rest },
    ref,
  ) {
    const track = useTrack()
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      track({
        component_id: trackId,
        component_type: trackType,
        action: trackAction,
        ...(trackMeta ? { metadata: trackMeta } : {}),
      })
      onClick?.(e)
    }
    return <button ref={ref} type={rest.type ?? 'button'} onClick={handleClick} {...rest} />
  },
)
